import React from "react";
import { initialize, Field, reduxForm, getFormValues, submit } from "redux-form";
import { SUBMIT_QR_CODE_FORM } from "forms/list";
import { isSecureURL, maxLength, required } from "forms/validators/validators";
import { ReduxControls } from "rhombus-react";
import { useDispatch, useSelector } from "react-redux";
import { ExtraParametersFields } from "./ExtraParametersFields";
import { onErrorGetQrCodeConfiguration, onSuccessGetQrCodeConfiguration, QrCodes } from "reducers/QrCodes";
import { SUBMIT_QR_MODES } from "utils/constants";
import { hasPermission } from "utils/common";

import "./SubmitQrCodeForm.css";

const MAX_LENGTH_QR_NAME = 246; // Maximum length for an Asset is 250. So, QR_NAME + ".png" should not exceed 250 characters.
const maxLengthQrName = maxLength(MAX_LENGTH_QR_NAME);
const MAX_LENGTH_URL_DESTINATION = 1024;
const maxLengthUrlDestination = maxLength(MAX_LENGTH_URL_DESTINATION);

const tooltipProps = {
	inline:false,
	icon:false,
	direction: "right",
	size:"large",
	iconSize:"small",
};

const domain = "Creative View";
const action = "Upload Assets";

const urlDestinationTooltip = {
	...tooltipProps,
	description:"URL destination is adjustable, change it anytime without having to reprint QR",
};

export const qrNameCharactersValidation = (value) => {
	if (/[^a-zA-Z0-9\-_~.]/.test(value)) {
		return "Field can only contain alphanumeric characters, dashes, underscores, dots, and tildes (~)";
	}
};


export const buildQrCodeGenApiRequestFromConfiguration = (qrCodeConfiguration) => {
	const redirectConfigurations = [];
	if (qrCodeConfiguration.toggleRedirectConfigurations) {
		redirectConfigurations.push({
			redirectCondition: "os",
			values: {
				android: qrCodeConfiguration.redirect.android,
				ios: qrCodeConfiguration.redirect.ios,
			},
		});
	}

	return {
		...qrCodeConfiguration,
		extraParams: qrCodeConfiguration.extraParams
			.filter((extraParam) => {return !!extraParam.value && !!extraParam.key;}),
		redirectConfigurations,
	};
};

export const onSubmitModal = async (formValues, dispatch, props) => {
	const { idQrCode, designId, mode, onSuccess, onClose } = props;
	const qrCodeGenerationBody = buildQrCodeGenApiRequestFromConfiguration(formValues);
	let actionToDispatch = QrCodes.generateQrCode(designId, qrCodeGenerationBody);
	if (mode === SUBMIT_QR_MODES.EDIT) {
		actionToDispatch = QrCodes.editQrCode(idQrCode, qrCodeGenerationBody);
	}
	const response = await dispatch(actionToDispatch);
	if (response.success) {
		await onSuccess();
		await onClose();
	}
};

function SubmitQrCodeForm(props) {
	const { idQrCode } = props;
	const formState = useSelector(getFormValues(SUBMIT_QR_CODE_FORM)) ?? {};
	const dispatch = useDispatch();
	const user = useSelector(state => state.you);
	const readOnly = !hasPermission(user, domain, action);

	const retrieveInitialQrCodeConfiguration = async () => {
		const resp = await dispatch(QrCodes.getQrCodeConfiguration(idQrCode));
		const qrCodeConfig = resp.success ? onSuccessGetQrCodeConfiguration(resp) : onErrorGetQrCodeConfiguration();
		if(props.mode === SUBMIT_QR_MODES.DUPLICATE) {
			qrCodeConfig.name = `CopyOf_${qrCodeConfig.name}`;
		}
		dispatch(initialize(SUBMIT_QR_CODE_FORM, qrCodeConfig));
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		dispatch(submit(SUBMIT_QR_CODE_FORM));
	};

	React.useEffect(() => {
		retrieveInitialQrCodeConfiguration();
	}, []);

	return (
		<form key="form" id={SUBMIT_QR_CODE_FORM} className={SUBMIT_QR_CODE_FORM} onSubmit={handleSubmit}>
			<Field
				name="name"
				type="text"
				placeholder="Enter name..."
				component={ReduxControls.Input}
				label="QR Code Name"
				charMax={MAX_LENGTH_QR_NAME}
				showCharCount
				required
				disabled={readOnly || props.mode === SUBMIT_QR_MODES.EDIT}
				className="xl-input-qrcode"
				validate={[maxLengthQrName, required, qrNameCharactersValidation]}
			/>
			<Field
				name="defaultTargetUrl"
				type="text"
				placeholder="Enter URL destination..."
				component={ReduxControls.Input}
				label="URL Destination"
				tip={urlDestinationTooltip}
				charMax={MAX_LENGTH_URL_DESTINATION}
				showCharCount
				required
				className="xl-input-qrcode"
				validate={[maxLengthUrlDestination, required, isSecureURL]}
				disabled={readOnly}
			/>
			<Field
				name="toggleRedirectConfigurations"
				component={ReduxControls.Toggle}
				type="toggle"
				label="Dynamic URL Destination"
				theme="blue"
				value={false}
				props={{
					className: "toggle-redirect-configurations",
				}}
				disabled={readOnly}
			/>
			{formState.toggleRedirectConfigurations && (<>
				<Field
					name="redirect.ios"
					type="text"
					placeholder="Enter Apple OS URL destination..."
					component={ReduxControls.Input}
					label="Apple OS URL Destination"
					charMax={MAX_LENGTH_URL_DESTINATION}
					showCharCount
					required
					className="xl-input-qrcode"
					validate={[maxLengthUrlDestination, required, isSecureURL]}
					disabled={readOnly}
				/>
				<Field
					name="redirect.android"
					type="text"
					placeholder="Enter Android OS URL destination..."
					component={ReduxControls.Input}
					label="Android OS URL Destination"
					charMax={MAX_LENGTH_URL_DESTINATION}
					showCharCount
					required
					className="xl-input-qrcode"
					validate={[maxLengthUrlDestination, required, isSecureURL]}
					disabled={readOnly}
				/>
			</>)}
			<ExtraParametersFields readOnly={readOnly} />
		</form>
	);
}

SubmitQrCodeForm = reduxForm({
	form: SUBMIT_QR_CODE_FORM,
	onSubmit: onSubmitModal,
})(SubmitQrCodeForm);

SubmitQrCodeForm.displayName = "SubmitQrCodeForm";

export default SubmitQrCodeForm;
